import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Loader2, Calendar, Clock, User, ChevronLeft, Facebook, Twitter, Linkedin, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE = window.location.origin || '';
const SITE_URL = 'https://goturkeytransfer.com';
const ORG_FALLBACK_AUTHOR = 'Go Turkey Transfer Editorial Team';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [schema, setSchema] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, location.pathname]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/cms/blog/post/${slug}`);
      const data = await response.json();

      if (data.success && data.post) {
        setPost(data.post);
        if (data.schema) {
          setSchema(data.schema);
        }
        if (data.post.category) {
          fetchRelatedPosts(data.post.category, data.post.id);
        }
      } else {
        // Fallback to CMS page API
        const cmsResponse = await fetch(`${API_BASE}/api/cms/page/blog/${slug}`);
        const cmsData = await cmsResponse.json();

        if (cmsData.success && cmsData.page) {
          setPost(cmsData.page);
        } else {
          setError('Blog post not found');
        }
      }
    } catch (err) {
      console.error('Failed to fetch blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedPosts = async (category, currentId) => {
    try {
      const response = await fetch(`${API_BASE}/api/cms/blog/posts?category=${encodeURIComponent(category)}&limit=3`);
      const data = await response.json();

      if (data.success && data.posts) {
        setRelatedPosts(data.posts.filter(p => p.id !== currentId).slice(0, 3));
      }
    } catch (err) {
      console.error('Failed to fetch related posts:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.includes('vipgez-premium.emergent.host')) {
      return url.replace('https://vipgez-premium.emergent.host', SITE_URL);
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${API_BASE}${url.startsWith('/') ? url : '/' + url}`;
  };

  // Derive truthful author display
  const getAuthorDisplay = () => {
    if (!post) return null;
    const raw = post.author || '';
    if (raw && raw !== 'GoTurkeyTransfer' && raw !== 'Go Turkey Transfer' && raw !== '') {
      return raw;
    }
    return ORG_FALLBACK_AUTHOR;
  };

  // Check if a date genuinely exists (not empty / null / undefined)
  const hasRealDate = (dateStr) => {
    return dateStr && dateStr.trim && dateStr.trim() !== '';
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  // === SEO META + SCHEMA INJECTION ===
  useEffect(() => {
    const setMeta = (selector, content) => {
      if (!content) return;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (selector.includes('name="')) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) meta.setAttribute('name', name);
        }
        if (selector.includes('property="')) {
          const property = selector.match(/property="([^"]+)"/)?.[1];
          if (property) meta.setAttribute('property', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const setCanonical = (href) => {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', href);
    };

    // Clean up any prior blog schema (including static HTML schema)
    const cleanExistingSchemas = () => {
      // Remove ALL JSON-LD scripts containing BlogPosting (both static and dynamic)
      const allSchemas = document.querySelectorAll('script[type="application/ld+json"]');
      allSchemas.forEach(el => {
        try {
          const content = JSON.parse(el.textContent);
          if (content['@graph'] && content['@graph'].some(n => n['@type'] === 'BlogPosting')) {
            el.remove();
          }
        } catch (e) {
          // Ignore parse errors
        }
      });
    };

    if (error || (!isLoading && !post)) {
      document.title = 'Blog Post Not Found | GoTurkeyTransfer';
      setMeta('meta[name="robots"]', 'noindex, nofollow');
      setMeta('meta[name="googlebot"]', 'noindex, nofollow');
      cleanExistingSchemas();
      return;
    }

    if (!post) return;

    const title = post.seoTitle || post.title || '';
    const description = post.metaDescription || post.excerpt || '';
    const canonical = `${SITE_URL}/blog/${slug}/`;
    const ogImage = post.coverImage ? getImageUrl(post.coverImage) : `${SITE_URL}/og-image.png`;

    document.title = `${title} | GoTurkeyTransfer Blog`;

    setMeta('meta[name="description"]', description);
    setMeta('meta[name="robots"]', 'index, follow');
    setMeta('meta[name="googlebot"]', 'index, follow');
    setCanonical(canonical);

    // Open Graph
    setMeta('meta[property="og:type"]', 'article');
    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[property="og:title"]', post.ogTitle || title);
    setMeta('meta[property="og:description"]', post.ogDescription || description);
    setMeta('meta[property="og:image"]', ogImage);

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', post.ogTitle || title);
    setMeta('meta[name="twitter:description"]', post.ogDescription || description);
    setMeta('meta[name="twitter:image"]', ogImage);

    // === INJECT @GRAPH SCHEMA ===
    cleanExistingSchemas();

    // If backend provided the schema, use it directly
    // Otherwise build a client-side fallback @graph
    const graphSchema = schema || buildClientSchema(post, slug, canonical);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-blog-schema', 'true');
    script.textContent = JSON.stringify(graphSchema);
    document.head.appendChild(script);
  }, [post, schema, error, isLoading, slug]);

  // Build a client-side @graph schema as fallback when backend doesn't provide one
  const buildClientSchema = (postData, postSlug, canonical) => {
    const title = postData.seoTitle || postData.title || '';
    const description = postData.metaDescription || postData.excerpt || '';
    const authorName = getAuthorDisplay();
    const isOrgAuthor = !postData.author || postData.author === 'GoTurkeyTransfer' || postData.author === 'Go Turkey Transfer' || postData.author === '';

    // BlogPosting node
    const blogPosting = {
      '@type': 'BlogPosting',
      '@id': `${canonical}#blogposting`,
      'headline': title,
      'description': description,
      'url': canonical,
      'inLanguage': postData.lang || 'en',
      'publisher': { '@id': `${SITE_URL}/#organization` },
      'mainEntityOfPage': { '@id': `${canonical}#webpage` },
      'author': isOrgAuthor
        ? { '@type': 'Organization', 'name': ORG_FALLBACK_AUTHOR }
        : { '@type': 'Person', 'name': authorName },
    };

    // Only add dates if they genuinely exist
    if (hasRealDate(postData.publishedDate)) {
      blogPosting.datePublished = postData.publishedDate;
    }
    if (hasRealDate(postData.updatedDate) && postData.updatedDate !== postData.publishedDate) {
      blogPosting.dateModified = postData.updatedDate;
    }

    // Image only if real URL
    const coverUrl = postData.coverImage ? getImageUrl(postData.coverImage) : '';
    if (coverUrl && coverUrl.startsWith('http')) {
      blogPosting.image = { '@type': 'ImageObject', 'url': coverUrl };
    }

    if (postData.category) blogPosting.articleSection = postData.category;
    if (postData.tags && postData.tags.length > 0) blogPosting.keywords = postData.tags;

    // WebPage node
    const webPage = {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      'url': canonical,
      'name': title,
      'description': description,
      'isPartOf': { '@id': `${SITE_URL}/#website` },
    };

    // BreadcrumbList node
    const breadcrumb = {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': SITE_URL },
        { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${SITE_URL}/blog/` },
        { '@type': 'ListItem', 'position': 3, 'name': title },
      ],
    };

    return { '@context': 'https://schema.org', '@graph': [blogPosting, webPage, breadcrumb] };
  };

  // === LOADING STATE ===
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 md:pt-36 pb-16">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" data-testid="blog-loading-spinner" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // === ERROR STATE ===
  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 md:pt-36 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Blog Post Not Found</h1>
            <p className="text-muted-foreground mb-8">The blog post you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/blog/">
              <Button data-testid="back-to-blog-btn">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Computed display values
  const authorDisplay = getAuthorDisplay();
  const publishedFormatted = formatDate(post.publishedDate);
  const updatedFormatted = (post.updatedDate && post.updatedDate !== post.publishedDate)
    ? formatDate(post.updatedDate)
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-32 md:pt-36 pb-16">
        <article className="container mx-auto px-4" data-testid="blog-article">
          {/* Breadcrumb Navigation */}
          <nav className="max-w-4xl mx-auto mb-6" aria-label="Breadcrumb" data-testid="blog-breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/blog/" className="hover:text-primary transition-colors">Blog</Link>
              </li>
              <li>/</li>
              <li className="text-foreground truncate max-w-[200px]">
                {post.title}
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <header className="max-w-4xl mx-auto mb-8" data-testid="blog-header">
            {post.category && (
              <span
                className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4"
                data-testid="blog-category"
              >
                {post.category}
              </span>
            )}

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6" data-testid="blog-title">
              {post.title}
            </h1>

            {/* Visible Metadata — matches schema exactly */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6" data-testid="blog-meta">
              {authorDisplay && (
                <span className="flex items-center gap-2" data-testid="blog-author">
                  <User className="h-4 w-4" />
                  {authorDisplay}
                </span>
              )}
              {publishedFormatted && (
                <span className="flex items-center gap-2" data-testid="blog-published-date">
                  <Calendar className="h-4 w-4" />
                  {publishedFormatted}
                </span>
              )}
              {updatedFormatted && (
                <span className="flex items-center gap-2" data-testid="blog-updated-date">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Updated: {updatedFormatted}
                </span>
              )}
              {post.readTime && (
                <span className="flex items-center gap-2" data-testid="blog-read-time">
                  <Clock className="h-4 w-4" />
                  {post.readTime}
                </span>
              )}
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Share:</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Share on Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Share on Twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </header>

          {/* Cover Image — only if genuine URL exists */}
          {post.coverImage && getImageUrl(post.coverImage).startsWith('http') && (
            <div className="max-w-4xl mx-auto mb-10" data-testid="blog-cover-image">
              <img
                src={getImageUrl(post.coverImage)}
                alt={post.title}
                className="w-full h-auto rounded-xl object-cover max-h-[500px]"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="max-w-4xl mx-auto">
            <div
              ref={contentRef}
              data-testid="blog-content"
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-foreground prose-headings:font-display
                prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-6 prose-h1:mt-0
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-strong:text-foreground
                prose-img:rounded-lg prose-img:mx-auto
                prose-li:text-muted-foreground
                prose-ul:text-muted-foreground
                prose-ol:text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: post.htmlContent || post.content || '' }}
            />
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="max-w-4xl mx-auto mt-10 pt-8 border-t border-border" data-testid="blog-tags">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Tags:</span>
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-secondary text-sm text-muted-foreground rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section with Internal Links */}
          <div className="max-w-4xl mx-auto mt-12 p-8 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl" data-testid="blog-cta">
            <h3 className="text-xl font-bold text-foreground mb-3 text-center">
              Ready to Book Your Transfer?
            </h3>
            <p className="text-muted-foreground mb-6 text-center">
              Experience premium airport transfer service across Turkey
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Link to="/istanbul-airport-transfer/" className="text-sm text-primary hover:underline">Istanbul Transfer</Link>
              <span className="text-muted-foreground">&bull;</span>
              <Link to="/antalya-airport-transfer/" className="text-sm text-primary hover:underline">Antalya Transfer</Link>
              <span className="text-muted-foreground">&bull;</span>
              <Link to="/dalaman-airport-transfer/" className="text-sm text-primary hover:underline">Dalaman Transfer</Link>
              <span className="text-muted-foreground">&bull;</span>
              <Link to="/bodrum-airport-transfer/" className="text-sm text-primary hover:underline">Bodrum Transfer</Link>
            </div>
            <div className="text-center">
              <Link to="/">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="blog-book-now-btn">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="max-w-4xl mx-auto mt-16" data-testid="blog-related-posts">
              <h2 className="text-2xl font-bold text-foreground mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={relatedPost.url || `/blog/${relatedPost.slug}`}
                    className="group block bg-secondary/50 rounded-xl overflow-hidden hover:bg-secondary/80 transition-colors"
                  >
                    {relatedPost.coverImage && (
                      <img
                        src={getImageUrl(relatedPost.coverImage)}
                        alt={relatedPost.title}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
